import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DateTime } from "luxon";
import { TimeRange, BookableTimeRanges } from "../../../types";
import { Colors } from "../../../Colors";

interface TimeRangeInputProps {
  day: string;
  timeRanges: TimeRange[];
  onChange: (ranges: TimeRange[]) => void;
}

export function TimeRangeInput({ day, timeRanges, onChange }: TimeRangeInputProps) {
  const handleStartTimeChange = (value: DateTime | null, index: number) => {
    const newRanges = [...timeRanges];
    newRanges[index] = {
      ...newRanges[index],
      startTime: value?.toFormat('HH:mm') || undefined
    };
    onChange(newRanges);
  };

  const handleEndTimeChange = (value: DateTime | null, index: number) => {
    const newRanges = [...timeRanges];
    newRanges[index] = {
      ...newRanges[index],
      endTime: value?.toFormat('HH:mm') || undefined
    };
    onChange(newRanges);
  };

  const addTimeRange = () => {
    onChange([...timeRanges, { 
      startTime: "09:00",
      endTime: "17:00"
    }]);
  };

  const removeTimeRange = (index: number) => {
    const newRanges = timeRanges.filter((_, i) => i !== index);
    onChange(newRanges);
  };

  const timePickerSx = {
    width: '200px',
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(30, 34, 45, 0.6)',
      '&:hover': {
        backgroundColor: 'rgba(30, 34, 45, 0.8)',
      }
    }
  };

  const buttonSx = {
    color: '#fff',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
    width: '40px',
    height: '40px'
  };

  return (
    <Box sx={{ 
      mb: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.5
    }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gap: 2,
        maxWidth: '800px',
        alignItems: 'center'
      }}>
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ 
            color: Colors.coolWhite,
          }}
        >
          {day.charAt(0).toUpperCase() + day.slice(1)}
        </Typography>

        {timeRanges.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={addTimeRange}
              sx={buttonSx}
            >
              <AddIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <TimePicker
              label="Start time"
              value={timeRanges[0].startTime ? DateTime.fromFormat(timeRanges[0].startTime, 'HH:mm') : null}
              onChange={(value) => handleStartTimeChange(value, 0)}
              sx={timePickerSx}
            />
            <TimePicker
              label="End time"
              value={timeRanges[0].endTime ? DateTime.fromFormat(timeRanges[0].endTime, 'HH:mm') : null}
              onChange={(value) => handleEndTimeChange(value, 0)}
              sx={timePickerSx}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                onClick={() => removeTimeRange(0)}
                sx={buttonSx}
              >
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton 
                onClick={addTimeRange}
                sx={buttonSx}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>

      {timeRanges.slice(1).map((range, index) => (
        <Box 
          key={index + 1} 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: '120px 1fr',
            gap: 2,
            alignItems: 'center',
            maxWidth: '800px'
          }}
        >
          <Box /> {/* Empty space for alignment */}
          <Box sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <TimePicker
              label="Start time"
              value={range.startTime ? DateTime.fromFormat(range.startTime, 'HH:mm') : null}
              onChange={(value) => handleStartTimeChange(value, index + 1)}
              sx={timePickerSx}
            />
            <TimePicker
              label="End time"
              value={range.endTime ? DateTime.fromFormat(range.endTime, 'HH:mm') : null}
              onChange={(value) => handleEndTimeChange(value, index + 1)}
              sx={timePickerSx}
            />
            <IconButton 
              onClick={() => removeTimeRange(index + 1)}
              sx={buttonSx}
            >
              <DeleteIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

interface BookableTimeRangesSectionProps {
  value: BookableTimeRanges;
  onChange: (ranges: BookableTimeRanges) => void;
  showRepeatedHelper?: boolean;
}

export default function BookableTimeRangesSection({ 
  value,
  onChange,
}: BookableTimeRangesSectionProps) {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  // All days for data structure
  const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Repeated time ranges helper state (only for main provider page)
  const [repeatedRanges, setRepeatedRanges] = useState<TimeRange[]>([
    { startTime: '09:00', endTime: '17:00' }
  ]);

  const handleRepeatedStartChange = (value: any, idx: number) => {
    const newRanges = [...repeatedRanges];
    newRanges[idx].startTime = value?.toFormat('HH:mm') || undefined;
    setRepeatedRanges(newRanges);
  };

  const handleRepeatedEndChange = (value: any, idx: number) => {
    const newRanges = [...repeatedRanges];
    newRanges[idx].endTime = value?.toFormat('HH:mm') || undefined;
    setRepeatedRanges(newRanges);
  };

  const addRepeatedRange = () => {
    setRepeatedRanges([...repeatedRanges, { startTime: '09:00', endTime: '17:00' }]);
  };

  const removeRepeatedRange = (idx: number) => {
    setRepeatedRanges(repeatedRanges.filter((_, i) => i !== idx));
  };

  const handleApplyRepeated = () => {
    const newRanges: BookableTimeRanges = { ...value };
    allDays.forEach(day => {
      newRanges[day as keyof BookableTimeRanges] = repeatedRanges.map(r => ({ ...r }));
    });
    onChange(newRanges);
  };

  const handleDayChange = (day: string, ranges: TimeRange[]) => {
    onChange({
      ...value,
      [day]: ranges
    });
  };

  // Initialize empty days with empty arrays for all days
  useEffect(() => {
    const initializedRanges = { ...value };
    let hasChanges = false;

    allDays.forEach(day => {
      if (!initializedRanges[day as keyof BookableTimeRanges]) {
        initializedRanges[day as keyof BookableTimeRanges] = [];
        hasChanges = true;
      }
    });

    if (hasChanges) {
      onChange(initializedRanges);
    }
  }, []);

  return (
    <Box display="flex" flexDirection="column" sx={{ mb: 3 }}>
      <Typography variant="body2" fontWeight="bold" sx={{ marginBottom: "8px", color: Colors.coolWhite }}>
        Bookable Time Ranges
      </Typography>
      
      {/* Repeated time range helper - only show on main provider page */}
        <Box sx={{ mt: "5px", mb: 2 }}>
          <Typography variant="caption" sx={{ color: Colors.info, mb: 1.5, display: 'block' }}>
            Set repeated time ranges and apply to all days (Mon-Sun)
          </Typography>
          {repeatedRanges.map((range, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <TimePicker
                label="Start time"
                value={range.startTime ? DateTime.fromFormat(range.startTime, 'HH:mm') : null}
                onChange={value => handleRepeatedStartChange(value, idx)}
                sx={{ width: '160px' }}
              />
              <TimePicker
                label="End time"
                value={range.endTime ? DateTime.fromFormat(range.endTime, 'HH:mm') : null}
                onChange={value => handleRepeatedEndChange(value, idx)}
                sx={{ width: '160px' }}
              />
              <IconButton onClick={() => removeRepeatedRange(idx)} disabled={repeatedRanges.length === 1} size="small">
                <DeleteIcon fontSize="small" />
              </IconButton>
              {idx === repeatedRanges.length - 1 && (
                <IconButton onClick={addRepeatedRange} size="small">
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            variant="contained"
            color="primary"
            onClick={handleApplyRepeated}
            sx={{ mt: 1, mb: 1, fontSize: 13, fontWeight: 500, textTransform: 'none' }}
          >
            Apply to All Days
          </Button>
        </Box>
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        {days.map((day) => (
          <TimeRangeInput
            key={day}
            day={day}
            timeRanges={value[day as keyof BookableTimeRanges] || []}
            onChange={(ranges) => handleDayChange(day, ranges)}
          />
        ))}
      </Box>
    </Box>
  );
} 